import { AppDate } from '../envTypes';

import { ModelBase } from './base';

export enum PopStoreSubscriptionLevel {
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

export enum PopStoreExtendedSubscriptionLevel {
  FOLLOWER = 'follower',
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

export enum PopStoreUserAccessLevel {
  ALL = 'all',
  SUBSCRIBER = 'subscriber',
  FAN = 'fan',
  VIP = 'vip',
}

export enum PopStoreExtendedUserAccessLevel {
  ALL = PopStoreUserAccessLevel.ALL,
  FOLLOWER = 'follower',
  SUBSCRIBER = PopStoreUserAccessLevel.SUBSCRIBER,
  FAN = PopStoreUserAccessLevel.FAN,
  VIP = PopStoreUserAccessLevel.VIP,
}

/**
 * Enum that represents the status of a subscription
 */
export enum PopStoreSubscriptionStatus {
  /** Unavailable, not configured yet */
  Pending = 0,

  /** Active, successfully configured */
  Active = 1,
}

export type SubscriptionPlatformLevelInfo = {
  // the id of this subscription level
  id: number;

  // platform subscription level definition metadata
  platform_sub_level_id: number;
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_precedence: number;
};

export type SubscriptionPriceInfo = {
  creator_sub_price_id: number;
  creator_sub_id: number;
  creator_sub_price_without_precision: number;
  creator_sub_price_currency: string;
  default_commitment_period: number;
  creator_sub_commitment_discount_percentage?: number | null;
  creator_sub_discount_commitment_period?: number | null;
  creator_sub_price_created_date?: string;
  creator_sub_price_end_date: string | null;
  creator_sub_current_price: boolean;
  creator_sub_activated_on: number;
  discount_for_longer_subscription_commitment?: boolean;
  created_at: string;
};

export type SubscriptionBasicInfo = {
  // id of the subscription configuration
  creator_sub_id: number;
  creator_store_id: string;
  platform_sub_level_id: number;
  creator_sub_name: string;
  creator_sub_description: string;
  creator_sub_primary_image: string | null;
  creator_sub_background_image: string | null;
  creator_sub_preview_video: string | null;
  created_at: string;
  active?: boolean;
};

export type SubscriptionConfiguration = {
  // subscription level information
  platform_subscription_level: SubscriptionPlatformLevelInfo;

  // subscription basic information
  creator_subscription: SubscriptionBasicInfo;

  // subscription pricing information
  creator_subscription_price: SubscriptionPriceInfo;
};

export type SubscriptionStatistics = {
  registered_users: number;
  paying_users: number;
  current_price: {
    currency: string;
    frequency: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  };
  previous_price?: {
    currency: string;
    frequency: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  } | null;
  expected_income?: {
    currency: string;
    frequency?: string;
    price_without_precision: number;
    currencyPrecisionFactor: number;
  } | null;
};

export type SubscriptionBase = {
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_display_name: string;
  platform_sub_level_id: number;
};

/**
 * Represents a placeholder subscription of popstore creator's subscriptions.
 * This is used when the subscription is not configured yet.
 */
export type PlaceholderSubscription = SubscriptionBase & {
  // subscription configuration status
  subscription_configuration_status: PopStoreSubscriptionStatus.Pending;
  payingUsers: number;
};

/**
 * Represents an active subscription of popstore creator's subscriptions.
 * This is used when the subscription is already successfully configured.
 */
export type ActiveSubscription = SubscriptionBase & {
  // subscription configuration status
  subscription_configuration_status: PopStoreSubscriptionStatus.Active;

  // subscription configuration details
  subscription_data: SubscriptionConfiguration;

  // creator subscription statistics data
  creator_subscription: SubscriptionStatistics;
};

type WithGeneralTimestamps<T> = T & {
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_at_timestamp: number;
  updated_at_timestamp: number;
  deleted_at_timestamp: number | null;
};

/**
 * Type that represents the information of a user subscription for a customer.
 */
export type PopStoreUserSubscription = {
  user_subscription_id: number;
  psl_user_id: string;
  creator_sub_price_id: number;
  creator_sub_id: number;
  subscription_start_date: number;
  subscription_end_date: string | null;
  subscription_renewal_date: number;
  subscription_status: 'active' | 'cancelled' | 'refunded';
  cancelled_at: number | null;
  last_charged: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_at_timestamp: number;
  updated_at_timestamp: number;
  deleted_at_timestamp: number | null;
  creator_subscription: WithGeneralTimestamps<SubscriptionBasicInfo> & {
    platform_subscription_level: SubscriptionPlatformLevelInfo;
  };
  creator_subscription_price: WithGeneralTimestamps<SubscriptionPriceInfo>;
};

/**
 * Represents a subscription of popstore creator's subscriptions,
 * which can be either placeholder (not configured yet) or active
 * (successfully configured).
 */
export type PopStoreSubscription = PlaceholderSubscription | ActiveSubscription;

export type PopStoreFollowerSubscription = {
  level: PopStoreExtendedSubscriptionLevel.FOLLOWER;
  description?: string;
  active?: boolean;
};

export type PopStorePlatformSubscription = {
  level: PopStoreSubscriptionLevel;
  subscription: PopStoreSubscription;
};

export type PopStoreExtendedSubscription =
  | PopStoreFollowerSubscription
  | PopStorePlatformSubscription;

export interface Subscriptions {
  fan: PopStoreSubscription;
  subscriber: PopStoreSubscription;
  vip: PopStoreSubscription;
}

export enum CreatorSubscriptionStatus {
  succeeded = 'succeeded',
  canceled = 'canceled',
  processing = 'processing',
  requires_action = 'requires_action',
  requires_capture = 'requires_capture',
  requires_confirmation = 'requires_confirmation',
  requires_payment_method = 'requires_payment_method',
  refunded = 'refunded',
}

export interface PopStoreUserSubscriptionInfo {
  psl_user_id: string;
  user_subscription_id: number;
  platform_sub_level: PopStoreSubscriptionLevel;
  platform_sub_level_precedence: number;
  subscription_end_date: number | null; // unix timestamp
  subscription_start_date: number; // unix timestamp
  creator_sub_description: string;
  subscription_status: 'active' | 'cancelled' | 'refunded';
}

export interface CreatorChargeDistribution {
  subtotal: number;
  taxAmount: number;
  commissionFee: number;
  gatewayProcessingFee: number;
  creatorNetIncome: number;
  commissionFeeRate: number;
  gatewayProcessingFeeRate: number;
  gatewayProcessingFeeExtra?: number;
}

export interface CreatorSubscriptionBalanceRecord extends ModelBase {
  subscriptionTransactionId: string;
  /**
   * the index of the balance record in the subscription transaction
   * start from 1
   */
  index: number;
  availableBalance: number;
  pendingBalance: number;
  nextAvailableDate?: AppDate;
  currency?: string;
}

export interface CreatorSubscriptionTransaction extends ModelBase {
  commentSoldSubscriptionId: string;
  status: CreatorSubscriptionStatus;
  currentPeriodStart: AppDate;
  currentPeriodEnd: AppDate;
  subscriptionLevel: PopStoreSubscriptionLevel;
  subscriptionInterval: string; // 6 month, 12 month
  subscriptionIntervalCount?: number; // unit: month
  subscriptionType: string; // 'new', 'renewal'
  price: number;
  creatorChargeDistribution: CreatorChargeDistribution;
  /**
   * the  time indicates the balance from pending to available
   */
  creatorBalanceAvailableAt?: AppDate;
  creatorMonthlyIncome?: number;
  latestBalanceRecord?: CreatorSubscriptionBalanceRecord;
  transactionInMultiCurrency?: CreatorSubscriptionTransactionInMultiCurrency;
  refundedAt?: AppDate;
}

export interface CreatorSubscriptionTransactionInMultiCurrency
  extends CreatorSubscriptionTransaction {
  currency?: string;
  exchangeRate?: number; // the exchange rate from USD to this currency
}

export interface CreatorSubscription extends ModelBase {
  commentSoldSubscriptionId: string;
  /**
   * The time at which the FIRST subscription was created
   */
  initialStartDate: AppDate;
  /**
   * The end time of the current period
   */
  endDate: AppDate;
  /**
   * Reserved field, not used for now
   */
  trialEnd: AppDate | null;
  subscriptionLevel: PopStoreSubscriptionLevel;
  latestTransactionId: string;
  latestTransactionUpdateDate: AppDate;
  isActive: boolean;
}
